import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUserStore } from "@/store/userStore";
import { CreditWarningDialog } from "./CreditWarningDialog";

type EditableTextProps = {
  value: string;
  onSave: (value: string) => Promise<void>;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  className?: string;
  inputClassName?: string;
  textComponent?: "input" | "textarea";
  isEditable?: boolean;
  requiredCredits?: number;
  showCreditWarning?: boolean;
  supportedLanguages?: string[];
  currentLanguage?: string;
  prefix?: string;
};

export function EditableText({
  value,
  onSave,
  isEditing,
  onEdit,
  onCancel,
  className,
  inputClassName,
  textComponent = "input",
  isEditable = true,
  requiredCredits = 0,
  showCreditWarning = false,
  supportedLanguages = [],
  currentLanguage = "",
  prefix,
}: EditableTextProps) {
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const { getRemainingCredits } = useUserStore();

  // Update editValue when value prop changes or when entering edit mode
  useEffect(() => {
    setEditValue(value);
  }, [value, isEditing]);

  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const checkCredits = () => {
    if (!showCreditWarning || requiredCredits === 0) return true;

    const remainingCredits = getRemainingCredits();
    if (remainingCredits < requiredCredits) {
      setShowCreditDialog(true);
      return false;
    }
    return true;
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!checkCredits()) return;

      setIsSaving(true);
      try {
        await onSave(editValue);
      } finally {
        setIsSaving(false);
      }
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  const handleSaveClick = async () => {
    if (!checkCredits()) return;

    setIsSaving(true);
    try {
      await onSave(editValue);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = () => {
    // Check credits before entering edit mode
    const remainingCredits = getRemainingCredits();
    if (
      showCreditWarning &&
      requiredCredits > 0 &&
      remainingCredits < requiredCredits
    ) {
      setShowCreditDialog(true);
      return;
    }
    onEdit();
  };

  if (isEditing && isEditable) {
    const InputComponent = textComponent === "textarea" ? Textarea : Input;
    return (
      <>
        <div className="flex items-center gap-2">
          {prefix ? (
            <div className="flex flex-1">
              <div className="bg-muted flex items-center justify-center px-3 py-2 h-10 rounded-l-md border border-r-0 border-input">
                <span className="text-muted-foreground">{prefix}</span>
              </div>
              <InputComponent
                ref={inputRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={cn("min-w-0 rounded-l-none flex-1", inputClassName)}
                disabled={isSaving}
              />
            </div>
          ) : (
            <InputComponent
              ref={inputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className={cn("min-w-0 flex-1", inputClassName)}
              disabled={isSaving}
            />
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={handleSaveClick}
              className="p-1 hover:bg-gray-100 rounded"
              disabled={isSaving}
            >
              {isSaving ? (
                <div className="h-4 w-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              ) : (
                <Check className="h-4 w-4 text-green-600" />
              )}
            </button>
            <button
              onClick={onCancel}
              className="p-1 hover:bg-gray-100 rounded"
              disabled={isSaving}
            >
              <X className="h-4 w-4 text-red-600" />
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="group/edit relative inline-flex items-center max-w-full">
        <div
          className={cn(
            isEditable ? "group-hover/edit:bg-muted/50 cursor-pointer" : "",
            "rounded px-1 -mx-1 py-0.5 transition-colors",
            className,
            !value && isEditable ? "italic text-muted-foreground" : ""
          )}
          onClick={isEditable ? handleEditClick : undefined}
        >
          {prefix
            ? `${prefix} ${value}`
            : value || (isEditable ? "Click to edit..." : "")}
        </div>
        {isEditable && (
          <button
            onClick={handleEditClick}
            className="opacity-0 group-hover/edit:opacity-100 ml-2 p-1 hover:bg-muted rounded transition-all"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>

      {showCreditWarning && supportedLanguages.length > 0 && (
        <CreditWarningDialog
          open={showCreditDialog}
          onOpenChange={setShowCreditDialog}
          requiredCredits={requiredCredits}
          supportedLanguages={supportedLanguages}
          currentLanguage={currentLanguage}
          onProceed={async (selectedLanguages) => {
            setShowCreditDialog(false);
            if (selectedLanguages.length > 0) {
              setIsSaving(true);
              try {
                await onSave(editValue);
              } finally {
                setIsSaving(false);
              }
            }
          }}
        />
      )}
    </>
  );
}

export default EditableText;
